import dynamic from 'next/dynamic';
import Link from 'next/link';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import type {SxProps, Theme} from '@mui/material/styles';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import DisplayImage from '@/components/Ui-components/display-image';
import Eyebrow from '@/components/Common/Eyebrow';
import {projectBriefContent, projectBriefFeatures} from '@/util/contents/contact-us';
import {borderRadius, colors, fontSize, fontWeight, lineHeightRatio, spacing} from '@/styles/tokens';
import {cdnUrl} from '@/util/cdnImage';
const ContactUsForm = dynamic(() => import('@/components/Ui-components/contactform'), {ssr: false, loading: () => null});

// MUI version of the bottom "project brief" section, kept out of main-layout so its MUI code
// only ships to pages that render it. The homepage uses the plain ProjectBriefSection instead.

// ─── Module-level static data (never changes, no reason to recreate per render) ──
// Project-brief left column — feature icons served from the CDN (contact-us-forms/) + sx.
const featureIconMap: Record<string, string> = {
    award: cdnUrl('contact-us-forms/years-platforms-delivered.svg'),
    lock: cdnUrl('contact-us-forms/nda-signed-on-day-one.svg'),
    bolt: cdnUrl('contact-us-forms/48-hour-scoped-estimate.svg'),
    team: cdnUrl('contact-us-forms/dedicated-account-managers.svg')
};
const PHONE_ICON = cdnUrl('contact-us-forms/phone-icon.svg');
const EMAIL_ICON = cdnUrl('contact-us-forms/email-icon.svg');

// ─── Contact popup dialog styling (owned by the call site, not globals.scss) ────
const briefHeadingSx = {
    fontSize: {xs: fontSize[36], md: fontSize[46]},
    fontWeight: fontWeight.extrabold,
    lineHeight: lineHeightRatio.flat,
    mb: 3
};
const briefDescriptionSx = {
    fontSize: fontSize[16],
    lineHeight: lineHeightRatio.relaxed,
    color: colors.text.secondary,
    maxWidth: 460,
    mb: 4
};
const featureIconBoxSx = {
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: borderRadius.md,
    background: colors.background.tag,
    color: colors.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};
const featureHeadingSx = {fontSize: fontSize[16], fontWeight: fontWeight.bold, mb: 0.5};
const featureTaglineSx = {fontSize: fontSize[14], lineHeight: lineHeightRatio.snug, color: colors.text.secondary};
const contactLineSx = {display: 'flex', alignItems: 'center', gap: 1, color: colors.text.primary, mb: 1.5};
const contactLinkSx = {fontSize: fontSize[15], fontWeight: fontWeight.medium, color: colors.text.primary, textDecoration: 'none'};
// Migrated from globals.scss `.contact-us-form-grid` (sole consumer — only ever referenced by
// this Grid item below).
const contactUsFormGridSx = {pt: `${spacing[40]} !important`} as const;
// ─────────────────────────────────────────────────────────────────────────────

export const renderLayoutMain = () => {
    return (
        <Box sx={{pt: {xs: 5, md: 7.5}}}>
            <MUIContainer>
                <Grid container spacing={1} className="contact-grid main-layout" sx={{justifyContent: 'space-between !important'}}>
                    <Grid item xs={12} sm={12} md={5} lg={5} sx={{flexBasis: {md: '45%'}, maxWidth: {md: '45%'}}}>
                        <Box>
                            <Eyebrow text={projectBriefContent.eyebrow} />
                            <Typography component="p" sx={briefHeadingSx} className="font-headers">
                                {projectBriefContent.headingLine1}
                                <br />
                                <Box component="span" sx={{color: colors.accent.orange}}>
                                    {projectBriefContent.headingLine2}
                                </Box>
                            </Typography>
                            <Typography component="p" sx={briefDescriptionSx} className="font-regular-text">
                                {projectBriefContent.description}
                            </Typography>

                            <Box mb={4}>
                                {projectBriefFeatures.map((item) => (
                                    <Box key={item.heading} sx={{display: 'flex', gap: 2, mb: 2.5}}>
                                        <Box sx={featureIconBoxSx}>
                                            <DisplayImage src={featureIconMap[item.icon]} width={20} height={20} alt={item.heading} />
                                        </Box>
                                        <Box>
                                            <Typography component="p" sx={featureHeadingSx} className="font-headers">
                                                {item.heading}
                                            </Typography>
                                            <Typography component="p" sx={featureTaglineSx} className="font-regular-text">
                                                {item.tagline}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>

                            <Box sx={contactLineSx}>
                                <DisplayImage src={PHONE_ICON} width={18} height={18} alt="Phone" />
                                <Box component="span" sx={contactLinkSx} className="font-regular-text">
                                    {projectBriefContent.phone.label}
                                </Box>
                            </Box>
                            <Box sx={contactLineSx}>
                                <DisplayImage src={EMAIL_ICON} width={18} height={18} alt="Email" />
                                <Link href={projectBriefContent.email.href} style={{textDecoration: 'none'}} target="_blank">
                                    <Box component="span" sx={contactLinkSx} className="font-regular-text">
                                        {projectBriefContent.email.label}
                                    </Box>
                                </Link>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={7}
                        lg={7}
                        className="contact-us-form-grid"
                        sx={[{flexBasis: {md: '55%'}, maxWidth: {md: '55%'}}, contactUsFormGridSx] as SxProps<Theme>}
                    >
                        <ContactUsForm />
                    </Grid>
                </Grid>
            </MUIContainer>
        </Box>
    );
};

const ProjectBrief = () => renderLayoutMain();

export default ProjectBrief;
